import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { RefreshCw, CheckCircle2, AlertCircle, Zap, ChevronDown, ChevronUp, List, AlertTriangle, Trash2, FileText } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { restartQuizGeneration } from '@/services/restartQuizGeneration';
import { restartPracticeGeneration } from '@/services/restartPracticeGeneration';

interface IntegrityStats {
  totalModules: number;
  // Actual matched counts (modules that HAVE content)
  modulesWithContent: number;
  modulesWithQuizzes: number;
  modulesWithLabs: number;
  // Total record counts (for orphan calculation)
  totalContentRecords: number;
  totalQuizRecords: number;
  totalLabRecords: number;
  // Missing lists
  missingContent: Array<{ course_id: string; module_id: string; module_title: string }>;
  missingQuizzes: Array<{ course_id: string; module_id: string; module_title: string }>;
  missingLabs: Array<{ course_id: string; module_id: string; module_title: string }>;
  // Orphaned counts
  orphanedContent: number;
  orphanedQuizzes: number;
  orphanedLabs: number;
}

export const DatabaseIntegrityMonitor = () => {
  const [stats, setStats] = useState<IntegrityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({ content: false, quizzes: false, labs: false });
  const [cleaning, setCleaning] = useState({ content: false, quizzes: false, labs: false });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showMissingContent, setShowMissingContent] = useState(false);
  const [showMissingQuizzes, setShowMissingQuizzes] = useState(false);
  const [showMissingLabs, setShowMissingLabs] = useState(false);

  const fetchIntegrityStats = async () => {
    try {
      // Get all enabled module IDs
      const { data: enabledModules, count: moduleCount } = await supabase
        .from('course_modules')
        .select('course_id, module_id, module_title', { count: 'exact' })
        .eq('is_enabled', true);

      const enabledModuleIds = enabledModules?.map(m => m.module_id) || [];
      const enabledModuleSet = new Set(enabledModuleIds);

      // Get ALL module IDs that exist (for true orphan detection)
      const { data: allModulesInDb } = await supabase
        .from('course_modules')
        .select('module_id');
      const allModuleIdsSet = new Set(allModulesInDb?.map(m => m.module_id) || []);

      // Get total record counts
      const { count: totalContentRecords } = await supabase
        .from('module_content')
        .select('*', { count: 'exact', head: true });

      const { count: totalQuizRecords } = await supabase
        .from('module_quizzes')
        .select('*', { count: 'exact', head: true });

      const { count: totalLabRecords } = await supabase
        .from('generated_practice_labs')
        .select('*', { count: 'exact', head: true });

      // Get content module IDs that MATCH enabled modules
      const { data: contentModuleIds } = await supabase
        .from('module_content')
        .select('module_id');
      const contentIds = new Set(contentModuleIds?.map(c => c.module_id) || []);
      const matchedContentIds = enabledModuleIds.filter(id => contentIds.has(id));

      // Get quiz module IDs that MATCH enabled modules
      const { data: quizModuleIds } = await supabase
        .from('module_quizzes')
        .select('module_id');
      const quizIds = new Set(quizModuleIds?.map(q => q.module_id) || []);
      const matchedQuizIds = enabledModuleIds.filter(id => quizIds.has(id));

      // Get lab module IDs that MATCH enabled modules
      const { data: labModuleIds } = await supabase
        .from('generated_practice_labs')
        .select('module_id');
      const labIds = new Set(labModuleIds?.map(l => l.module_id) || []);
      const matchedLabIds = enabledModuleIds.filter(id => labIds.has(id));

      // Find modules MISSING content/quizzes/labs
      const missingContent = enabledModules?.filter(m => !contentIds.has(m.module_id)) || [];
      const missingQuizzes = enabledModules?.filter(m => !quizIds.has(m.module_id)) || [];
      const missingLabs = enabledModules?.filter(m => !labIds.has(m.module_id)) || [];

      // Calculate TRULY ORPHANED records (module_id doesn't exist in course_modules AT ALL)
      const orphanedContent = (contentModuleIds?.filter(c => !allModuleIdsSet.has(c.module_id)) || []).length;
      const orphanedQuizzes = (quizModuleIds?.filter(q => !allModuleIdsSet.has(q.module_id)) || []).length;
      const orphanedLabs = (labModuleIds?.filter(l => !allModuleIdsSet.has(l.module_id)) || []).length;

      setStats({
        totalModules: moduleCount || 0,
        modulesWithContent: matchedContentIds.length,
        modulesWithQuizzes: matchedQuizIds.length,
        modulesWithLabs: matchedLabIds.length,
        totalContentRecords: totalContentRecords || 0,
        totalQuizRecords: totalQuizRecords || 0,
        totalLabRecords: totalLabRecords || 0,
        missingContent,
        missingQuizzes,
        missingLabs,
        orphanedContent,
        orphanedQuizzes,
        orphanedLabs
      });
    } catch (error) {
      console.error('Failed to fetch integrity stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch database integrity stats',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrityStats();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchIntegrityStats, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleGenerateContent = async () => {
    setGenerating(prev => ({ ...prev, content: true }));
    try {
      // Call the fill-true-content-gaps edge function
      const { data, error } = await supabase.functions.invoke('fill-true-content-gaps', {
        body: { maxModules: 50 }
      });
      
      if (error) throw error;
      
      toast({
        title: 'Content Generation Started',
        description: `Generating content for ${stats?.missingContent.length || 0} modules`,
      });
      setTimeout(fetchIntegrityStats, 2000);
    } catch (error) {
      console.error('Content generation error:', error);
      toast({
        title: 'Error',
        description: 'Failed to start content generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, content: false }));
    }
  };

  const handleGenerateQuizzes = async () => {
    setGenerating(prev => ({ ...prev, quizzes: true }));
    try {
      await restartQuizGeneration();
      toast({
        title: 'Quiz Generation Started',
        description: `Generating quizzes for ${stats?.missingQuizzes.length || 0} modules`,
      });
      setTimeout(fetchIntegrityStats, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start quiz generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, quizzes: false }));
    }
  };

  const handleGenerateLabs = async () => {
    setGenerating(prev => ({ ...prev, labs: true }));
    try {
      await restartPracticeGeneration();
      toast({
        title: 'Practice Lab Generation Started',
        description: `Generating practice labs for ${stats?.missingLabs.length || 0} modules`,
      });
      setTimeout(fetchIntegrityStats, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start practice lab generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, labs: false }));
    }
  };

  const handleCleanOrphanedContent = async () => {
    if (!stats || stats.orphanedContent === 0) return;
    
    setCleaning(prev => ({ ...prev, content: true }));
    try {
      // Get enabled module IDs
      const { data: enabledModules } = await supabase
        .from('course_modules')
        .select('module_id')
        .eq('is_enabled', true);
      
      const enabledIds = enabledModules?.map(m => m.module_id) || [];
      
      // Get orphaned content records
      const { data: allContent } = await supabase
        .from('module_content')
        .select('id, module_id, content, course_id');
      
      const orphanedRecords = allContent?.filter(c => !enabledIds.includes(c.module_id)) || [];
      
      // Archive to ai_archived_content
      for (const record of orphanedRecords) {
        await supabase.from('ai_archived_content').insert({
          source_type: 'module_content',
          source_id: record.id,
          content: record.content?.substring(0, 10000), // Limit content size
          title: `Orphaned content from ${record.module_id}`,
          tags: ['orphaned', 'cleanup'],
          metadata: { course_id: record.course_id, module_id: record.module_id }
        });
      }
      
      // Delete orphaned records
      const orphanedIds = orphanedRecords.map(r => r.id);
      if (orphanedIds.length > 0) {
        await supabase.from('module_content').delete().in('id', orphanedIds);
      }
      
      toast({
        title: 'Cleanup Complete',
        description: `Archived and removed ${orphanedRecords.length} orphaned content records`,
      });
      
      fetchIntegrityStats();
    } catch (error) {
      console.error('Content cleanup error:', error);
      toast({
        title: 'Error',
        description: 'Failed to clean orphaned content',
        variant: 'destructive'
      });
    } finally {
      setCleaning(prev => ({ ...prev, content: false }));
    }
  };

  const handleCleanOrphanedQuizzes = async () => {
    if (!stats || stats.orphanedQuizzes === 0) return;
    
    setCleaning(prev => ({ ...prev, quizzes: true }));
    try {
      // Get enabled module IDs
      const { data: enabledModules } = await supabase
        .from('course_modules')
        .select('module_id')
        .eq('is_enabled', true);
      
      const enabledIds = enabledModules?.map(m => m.module_id) || [];
      
      // Get orphaned quiz records
      const { data: allQuizzes } = await supabase
        .from('module_quizzes')
        .select('id, module_id');
      
      const orphanedRecords = allQuizzes?.filter(q => !enabledIds.includes(q.module_id)) || [];
      const orphanedIds = orphanedRecords.map(r => r.id);
      
      if (orphanedIds.length > 0) {
        await supabase.from('module_quizzes').delete().in('id', orphanedIds);
      }
      
      toast({
        title: 'Cleanup Complete',
        description: `Removed ${orphanedRecords.length} orphaned quiz records`,
      });
      
      fetchIntegrityStats();
    } catch (error) {
      console.error('Quiz cleanup error:', error);
      toast({
        title: 'Error',
        description: 'Failed to clean orphaned quizzes',
        variant: 'destructive'
      });
    } finally {
      setCleaning(prev => ({ ...prev, quizzes: false }));
    }
  };

  const handleCleanOrphanedLabs = async () => {
    if (!stats || stats.orphanedLabs === 0) return;
    
    setCleaning(prev => ({ ...prev, labs: true }));
    try {
      // Get enabled module IDs
      const { data: enabledModules } = await supabase
        .from('course_modules')
        .select('module_id')
        .eq('is_enabled', true);
      
      const enabledIds = enabledModules?.map(m => m.module_id) || [];
      
      // Get orphaned lab records
      const { data: allLabs } = await supabase
        .from('generated_practice_labs')
        .select('id, module_id');
      
      const orphanedRecords = allLabs?.filter(l => !enabledIds.includes(l.module_id)) || [];
      const orphanedIds = orphanedRecords.map(r => r.id);
      
      if (orphanedIds.length > 0) {
        await supabase.from('generated_practice_labs').delete().in('id', orphanedIds);
      }
      
      toast({
        title: 'Cleanup Complete',
        description: `Removed ${orphanedRecords.length} orphaned lab records`,
      });
      
      fetchIntegrityStats();
    } catch (error) {
      console.error('Lab cleanup error:', error);
      toast({
        title: 'Error',
        description: 'Failed to clean orphaned labs',
        variant: 'destructive'
      });
    } finally {
      setCleaning(prev => ({ ...prev, labs: false }));
    }
  };

  const getCompletionPercentage = (matched: number, total: number) => {
    if (total === 0) return 100;
    return Math.round((matched / total) * 100);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Integrity Stats...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!stats) return null;

  const contentPercentage = getCompletionPercentage(stats.modulesWithContent, stats.totalModules);
  const quizPercentage = getCompletionPercentage(stats.modulesWithQuizzes, stats.totalModules);
  const labPercentage = getCompletionPercentage(stats.modulesWithLabs, stats.totalModules);
  
  const totalOrphaned = stats.orphanedContent + stats.orphanedQuizzes + stats.orphanedLabs;
  const totalMissing = stats.missingContent.length + stats.missingQuizzes.length + stats.missingLabs.length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Content Integrity Monitor</CardTitle>
            <CardDescription>Real-time content completeness tracking ({stats.totalModules} enabled modules)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}>
              <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchIntegrityStats}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Module Content Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {stats.missingContent.length === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <AlertCircle className="h-5 w-5 text-warning" />
              )}
              <span className="font-medium">Module Content</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={contentPercentage === 100 ? "default" : "destructive"}>
                {stats.modulesWithContent} / {stats.totalModules} ({contentPercentage}%)
              </Badge>
              {stats.orphanedContent > 0 && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-500">
                  +{stats.orphanedContent} orphaned
                </Badge>
              )}
              {stats.missingContent.length > 0 && (
                <Button size="sm" onClick={handleGenerateContent} disabled={generating.content}>
                  <FileText className="h-4 w-4 mr-1" />
                  {generating.content ? 'Generating...' : `Generate ${stats.missingContent.length}`}
                </Button>
              )}
            </div>
          </div>
          <Progress value={contentPercentage} className="h-2" />
          
          {stats.missingContent.length > 0 && (
            <Collapsible open={showMissingContent} onOpenChange={setShowMissingContent}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Show {stats.missingContent.length} missing content
                  </span>
                  {showMissingContent ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="space-y-1 max-h-60 overflow-y-auto border rounded-md p-2">
                  {stats.missingContent.slice(0, 50).map((module) => (
                    <div key={module.module_id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{module.module_title}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{module.module_id}</span>
                      </div>
                      <Badge variant="outline" className="ml-2 text-xs">{module.course_id}</Badge>
                    </div>
                  ))}
                  {stats.missingContent.length > 50 && (
                    <div className="text-center text-muted-foreground text-sm py-2">
                      ... and {stats.missingContent.length - 50} more
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Module Quizzes Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {stats.missingQuizzes.length === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <AlertCircle className="h-5 w-5 text-warning" />
              )}
              <span className="font-medium">Module Quizzes</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={quizPercentage === 100 ? "default" : "destructive"}>
                {stats.modulesWithQuizzes} / {stats.totalModules} ({quizPercentage}%)
              </Badge>
              {stats.orphanedQuizzes > 0 && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-500">
                  +{stats.orphanedQuizzes} orphaned
                </Badge>
              )}
              {stats.missingQuizzes.length > 0 && (
                <Button size="sm" onClick={handleGenerateQuizzes} disabled={generating.quizzes}>
                  <Zap className="h-4 w-4 mr-1" />
                  {generating.quizzes ? 'Generating...' : `Generate ${stats.missingQuizzes.length}`}
                </Button>
              )}
            </div>
          </div>
          <Progress value={quizPercentage} className="h-2" />
          
          {stats.missingQuizzes.length > 0 && (
            <Collapsible open={showMissingQuizzes} onOpenChange={setShowMissingQuizzes}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Show {stats.missingQuizzes.length} missing quizzes
                  </span>
                  {showMissingQuizzes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="space-y-1 max-h-60 overflow-y-auto border rounded-md p-2">
                  {stats.missingQuizzes.slice(0, 50).map((module) => (
                    <div key={module.module_id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{module.module_title}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{module.module_id}</span>
                      </div>
                      <Badge variant="outline" className="ml-2 text-xs">{module.course_id}</Badge>
                    </div>
                  ))}
                  {stats.missingQuizzes.length > 50 && (
                    <div className="text-center text-muted-foreground text-sm py-2">
                      ... and {stats.missingQuizzes.length - 50} more
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Practice Labs Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {stats.missingLabs.length === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <AlertCircle className="h-5 w-5 text-warning" />
              )}
              <span className="font-medium">Practice Labs</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={labPercentage === 100 ? "default" : "destructive"}>
                {stats.modulesWithLabs} / {stats.totalModules} ({labPercentage}%)
              </Badge>
              {stats.orphanedLabs > 0 && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-500">
                  +{stats.orphanedLabs} orphaned
                </Badge>
              )}
              {stats.missingLabs.length > 0 && (
                <Button size="sm" onClick={handleGenerateLabs} disabled={generating.labs}>
                  <Zap className="h-4 w-4 mr-1" />
                  {generating.labs ? 'Generating...' : `Generate ${stats.missingLabs.length}`}
                </Button>
              )}
            </div>
          </div>
          <Progress value={labPercentage} className="h-2" />
          
          {stats.missingLabs.length > 0 && (
            <Collapsible open={showMissingLabs} onOpenChange={setShowMissingLabs}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Show {stats.missingLabs.length} missing labs
                  </span>
                  {showMissingLabs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="space-y-1 max-h-60 overflow-y-auto border rounded-md p-2">
                  {stats.missingLabs.slice(0, 50).map((module) => (
                    <div key={module.module_id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{module.module_title}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{module.module_id}</span>
                      </div>
                      <Badge variant="outline" className="ml-2 text-xs">{module.course_id}</Badge>
                    </div>
                  ))}
                  {stats.missingLabs.length > 50 && (
                    <div className="text-center text-muted-foreground text-sm py-2">
                      ... and {stats.missingLabs.length - 50} more
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Data Quality / Orphaned Records Section */}
        {totalOrphaned > 0 && (
          <div className="pt-4 border-t space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                <span className="text-sm font-medium">Orphaned Records (not linked to enabled modules)</span>
              </div>
              <Badge variant="outline" className="text-yellow-600 border-yellow-500">
                {totalOrphaned} total
              </Badge>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {stats.orphanedContent > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded border border-yellow-200 dark:border-yellow-800">
                  <div>
                    <span className="text-sm font-medium">Content</span>
                    <Badge variant="outline" className="ml-2 text-yellow-600">{stats.orphanedContent}</Badge>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleCleanOrphanedContent}
                    disabled={cleaning.content}
                    className="text-yellow-600 border-yellow-500 hover:bg-yellow-100"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {cleaning.content ? 'Cleaning...' : 'Clean'}
                  </Button>
                </div>
              )}
              
              {stats.orphanedQuizzes > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded border border-yellow-200 dark:border-yellow-800">
                  <div>
                    <span className="text-sm font-medium">Quizzes</span>
                    <Badge variant="outline" className="ml-2 text-yellow-600">{stats.orphanedQuizzes}</Badge>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleCleanOrphanedQuizzes}
                    disabled={cleaning.quizzes}
                    className="text-yellow-600 border-yellow-500 hover:bg-yellow-100"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {cleaning.quizzes ? 'Cleaning...' : 'Clean'}
                  </Button>
                </div>
              )}
              
              {stats.orphanedLabs > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded border border-yellow-200 dark:border-yellow-800">
                  <div>
                    <span className="text-sm font-medium">Labs</span>
                    <Badge variant="outline" className="ml-2 text-yellow-600">{stats.orphanedLabs}</Badge>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleCleanOrphanedLabs}
                    disabled={cleaning.labs}
                    className="text-yellow-600 border-yellow-500 hover:bg-yellow-100"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {cleaning.labs ? 'Cleaning...' : 'Clean'}
                  </Button>
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              Orphaned records exist in the database but their module_id doesn't match any enabled module.
              Cleaning will archive content records and delete quiz/lab records.
            </p>
          </div>
        )}

        {/* Overall Status */}
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Overall Status</span>
            <div className="flex items-center gap-2">
              {totalOrphaned > 0 && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-500">
                  {totalOrphaned} orphaned
                </Badge>
              )}
              {totalMissing > 0 && (
                <Badge variant="destructive">
                  {totalMissing} missing
                </Badge>
              )}
              {totalMissing === 0 && totalOrphaned === 0 && (
                <Badge variant="default">
                  All Content Complete
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DatabaseIntegrityMonitor;
